import React, { useEffect } from "react";
import * as T from "./types";
import {
  ICommonAttributes,
  IElement,
  IRawProps,
} from "@routes/private/Editor/types";
import { getParentCategoryColor } from "@utils/selectDescribe";
import { insideSelector } from "@utils/selector";
import { IResizeValues } from "../../CommonCommandsOptions/ResizeCommandOption/types";
import ImageCanvasElement from "../ImageCanvasElement";
import ShapeCanvasElement from "../ShapeCanvasElement";
import TextCanvasElement from "../TextCanvasElement";
import HiddenCanvasElement from "../HiddenCanvasElement";

const ElementsFlattener: React.FC<T.IProps> = (props) => {
  const {
    editorStatus,
    selectDescribe,
    entityInformation,
    selectorActive,
    artboardsZIndexChain,
    dataElement,
    artboardIndex,
    dsConfig,
    selectorProps,
    selectedNodes,
    onCommandChange,
    onOverElementIdChange,
    onSelectedElementIsEditing,
    onRotation,
    isSelected,
    handleDrag,
    handleSelect,
    showTransfomerOutside,
    handleResize,
    handleDragEnd,
    handleShowCrop,
  } = props;

  const [flattenedElements, setFlattenedElements] = React.useState<
    T.IFlattenedElement[]
  >([]);

  const finishFlattenElements = (
    level: IElement,
    describedLayerColor: string,
    hidden: boolean,
    rawProps: IRawProps[]
  ) => {
    let newFlattenedElements: T.IFlattenedElement[] = [];
    level.children?.forEach((element: IElement, index: number) => {
      const newDescribedLayerColor = !editorStatus.showDescribedLayerColor
        ? ""
        : getParentCategoryColor(
            element.id,
            dsConfig.selectDescribeConfig.categories,
            selectDescribe?.templateFields,
            selectDescribe?.taggedFields
          ) ?? describedLayerColor;
      const newRawProps = [...rawProps];
      const childAttributes = element.attributes as ICommonAttributes;
      const newHidden = hidden || childAttributes.hidden;
      if (childAttributes.rawProps?.length && !newHidden) {
        let mask = childAttributes.rawProps[0]?.mask;
        let vector = childAttributes.rawProps[0]?.vector;
        if (childAttributes.clipping === true && level.children) {
          let maskIndex = index;
          level.children?.forEach(
            (maskChild: IElement, mapMaskIndex: number) => {
              if (
                mapMaskIndex < maskIndex &&
                maskChild.attributes.clipping === false
              ) {
                maskIndex = mapMaskIndex;
              }
            }
          );
          let maskProps = level.children[maskIndex];
          mask = {
            id: maskProps.id,
            disabled: false,
            left: maskProps.attributes.x,
            top: maskProps.attributes.y,
            right: maskProps.attributes.x + maskProps.attributes.width,
            bottom: maskProps.attributes.y + maskProps.attributes.height,
          };
          if (level.children[maskIndex].attributes.rawVector) {
            vector = level.children[maskIndex].attributes.rawVector;
          }
        }
        newRawProps.push({
          mask: mask,
          effects: childAttributes.rawProps[0].effects,
          vector: vector,
        });
      }
      const flattenedElement: T.IFlattenedElement = {
        ...element,
        newDescribedLayerColor,
        attributes: {
          ...element.attributes,
          hidden: newHidden,
          rawProps: newRawProps,
        },
      };
      if (flattenedElement.type === "Group") {
        newFlattenedElements = newFlattenedElements.concat(
          finishFlattenElements(
            flattenedElement,
            newDescribedLayerColor,
            newHidden,
            newRawProps
          )
        );
      }
      newFlattenedElements.push(flattenedElement);
    });
    return newFlattenedElements;
  };

  const flattenElements = (level: IElement) => {
    return finishFlattenElements(
      level,
      "",
      level.attributes.hidden,
      level.attributes.rawProps ?? []
    );
  };

  useEffect(() => {
    setFlattenedElements(flattenElements(dataElement));
  }, [dataElement, editorStatus.showDescribedLayerColor, selectDescribe]);

  return (
    <>
      {flattenedElements.map((element) => {
        if (element.attributes.hidden) {
          return (
            <HiddenCanvasElement
              key={element.id}
              id={element.id}
              zIndexChain={artboardsZIndexChain[artboardIndex]?.zIndexChain}
            />
          );
        } else {
          const commonProps: T.ICommonProps = {
            key: element.id,
            element: element,
            containerId: dataElement.id,
            describedLayerColor: element.newDescribedLayerColor,
            zIndexChain: artboardsZIndexChain[artboardIndex]?.zIndexChain,
            isSelected: isSelected(element.id),
            isOver: editorStatus.overElementId === element.id,
            isSelectDescribe: editorStatus.showSelectDescribe,
            isCategorySelected:
              editorStatus.selectedSelectDescribeCategoryId !== undefined,
            editMode: editorStatus.editMode,
            isSelectorActive: selectorProps.visible ?? false,
            zoom: editorStatus?.zoom,
            isDragging: editorStatus.dragging,
            onDrag: (shapeRef: any, trRef: any) =>
              handleDrag(shapeRef, trRef, dataElement.id),
            onSelect: () =>
              !editorStatus.dragging &&
              editorStatus.editMode &&
              handleSelect(element.id),
            showTransfomerOutside: (
              trRef: any,
              shapeRef: any,
              selected: boolean
            ) => showTransfomerOutside(trRef, shapeRef, selected),
            onResize: (resizeValues: IResizeValues, notSave?: boolean) =>
              handleResize(resizeValues, element.id, dataElement.id, notSave),
            onOver: (isOver: boolean) =>
              !editorStatus.dragging &&
              editorStatus.editMode &&
              onOverElementIdChange(element.id, isOver),
            onRotation: (
              rotation?: number,
              x?: number,
              y?: number,
              offsetX?: number,
              offsetY?: number
            ) => onRotation(rotation, x, y, offsetX, offsetY),
            onDragEnd: (
              shapeRef: any,
              trRef: any,
              resizeValues: IResizeValues
            ) =>
              handleDragEnd(shapeRef, trRef, dataElement.id, resizeValues, [
                element.id,
              ]),
            isMultipleSelection:
              editorStatus.selectedElementsId.length > 1 ||
              insideSelector(selectorProps, element.id, selectedNodes),
          };
          switch (element.type) {
            case "Text":
              return (
                <TextCanvasElement
                  {...commonProps}
                  isEditing={editorStatus.selectedElementIsEditing}
                  onResizePoint={(resizeValues: IResizeValues) =>
                    onCommandChange(
                      "resize",
                      resizeValues,
                      [element.id],
                      undefined,
                      undefined,
                      undefined,
                      false
                    )
                  }
                  onChangeColor={(color: string) =>
                    onCommandChange(
                      "color",
                      color,
                      [element.id],
                      undefined,
                      undefined,
                      undefined,
                      true
                    )
                  }
                  onEdit={(
                    isEditing?: boolean,
                    offsetX?: number,
                    offsetY?: number,
                    height?: number
                  ) =>
                    onSelectedElementIsEditing(
                      element.id,
                      isEditing,
                      offsetX,
                      offsetY,
                      height
                    )
                  }
                />
              );
            case "Image":
              return (
                <ImageCanvasElement
                  {...commonProps}
                  folder={entityInformation.parsedTree}
                  isCropping={
                    editorStatus.showCropImage && isSelected(element.id)
                  }
                  onCommandChange={(command: any, value: any) =>
                    onCommandChange(command, value)
                  }
                  onShowCropImage={(
                    show: boolean,
                    cropMainGroupRef: any,
                    cropTrRef: any
                  ) => handleShowCrop(show, cropMainGroupRef, cropTrRef)}
                  selectorActive={selectorActive === "active"}
                />
              );
            case "Shape":
              return (
                <ShapeCanvasElement
                  {...commonProps}
                  folder={entityInformation.parsedTree}
                />
              );
          }
        }
      })}
    </>
  );
};

export default ElementsFlattener;
